import { lazy, Suspense } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ScrollToTop } from "./components/ScrollToTop";
import Layout from "./components/Layout";
import { AuthProvider } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";
import { CurrencyProvider } from "./contexts/CurrencyContext";
import { HelmetProvider } from "react-helmet-async";

// Eagerly load frequently visited pages
import Pagrindinis from "./pages/Pagrindinis";

// Lazy-load remaining pages
const Apie = lazy(() => import("./pages/Apie"));
const Auth = lazy(() => import("./pages/Auth"));
const Profilis = lazy(() => import("./pages/Profilis"));
const Admin = lazy(() => import("./pages/Admin"));
const NaudojimoSalygos = lazy(() => import("./pages/NaudojimoSalygos"));
const PrivatumoPolitika = lazy(() => import("./pages/PrivatumoPolitika"));
const Aciu = lazy(() => import("./pages/Aciu"));
const Instaliuoti = lazy(() => import("./pages/Instaliuoti"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const PriimtiKvietima = lazy(() => import("./pages/PriimtiKvietima"));
const DovanaGauta = lazy(() => import("./pages/DovanaGauta"));
const Zemelapis = lazy(() => import("./pages/Zemelapis"));
const ZemelapisSolo = lazy(() => import("./pages/ZemelapisSolo"));
const ZemelapisPildyti = lazy(() => import("./pages/ZemelapisPildyti"));
const PorosZemelapis = lazy(() => import("./pages/PorosZemelapis"));
const PorosZemelapisKodas = lazy(() => import("./pages/PorosZemelapisKodas"));
const PorosZemelapisPildyti = lazy(() => import("./pages/PorosZemelapisPildyti"));
const Archetipas = lazy(() => import("./pages/Archetipas"));
const ArchetipasAnketa = lazy(() => import("./pages/ArchetipasAnketa"));
const ArchetipasResult = lazy(() => import("./pages/ArchetipasResult"));
const Naujienlaiskis = lazy(() => import("./pages/Naujienlaiskis"));
const NaujienlaiskisLetter = lazy(() => import("./pages/NaujienlaiskisLetter"));

// Redirects a legacy path (including its params/query) to the new English path
const LegacyRedirect = ({ from, to }: { from: string; to: string }) => {
  const { pathname, search } = useLocation();
  return <Navigate to={`${pathname.replace(from, to)}${search}`} replace />;
};



const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

const App = () => (
  <HelmetProvider>
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <CurrencyProvider>
      <CartProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <Suspense fallback={<div className="flex-1 flex items-center justify-center min-h-[60vh]"><div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" /></div>}>
              <Routes>
                <Route path="/" element={<Layout />}>
                  <Route index element={<Pagrindinis />} />
                  <Route path="about" element={<Apie />} />
                  <Route path="pamokos" element={<Navigate to="/" replace />} />
                  <Route path="paskaitos" element={<Navigate to="/" replace />} />

                  <Route path="pamokosnew" element={<Navigate to="/" replace />} />
                  <Route path="pamokosnew/*" element={<Navigate to="/" replace />} />
                  <Route path="auth" element={<Auth />} />
                  <Route path="profile" element={<Profilis />} />
                  <Route path="admin" element={<Admin />} />
                  <Route path="terms" element={<NaudojimoSalygos />} />
                  <Route path="privacy" element={<PrivatumoPolitika />} />
                  <Route path="knygynas" element={<Navigate to="/" replace />} />
                  <Route path="knygynas/:slug" element={<Navigate to="/" replace />} />
                  <Route path="blogas" element={<Navigate to="/" replace />} />
                  <Route path="blogas/:slug" element={<Navigate to="/" replace />} />
                  <Route path="thank-you" element={<Aciu />} />
                  <Route path="install" element={<Instaliuoti />} />
                  <Route path="preke/:slug" element={<Navigate to="/" replace />} />
                  <Route path="unsubscribe" element={<Unsubscribe />} />
                  <Route path="accept-invitation" element={<PriimtiKvietima />} />
                  <Route path="gift-received" element={<DovanaGauta />} />
                  <Route path="map" element={<Zemelapis />} />
                  <Route path="map/solo" element={<ZemelapisSolo />} />
                  <Route path="map/fill/:sessionId" element={<ZemelapisPildyti />} />
                  <Route path="couples-map" element={<PorosZemelapis />} />
                  <Route path="couples-map/code" element={<PorosZemelapisKodas />} />
                  <Route path="couples-map/fill/:pairId" element={<PorosZemelapisPildyti />} />
                  <Route path="archetype" element={<Archetipas />} />
                  <Route path="archetype/details" element={<ArchetipasAnketa />} />
                  <Route path="archetype/:slug" element={<ArchetipasResult />} />

                  {/* Legacy Lithuanian paths → English paths */}
                  <Route path="apie" element={<Navigate to="/about" replace />} />
                  <Route path="profilis" element={<Navigate to="/profile" replace />} />
                  <Route path="naudojimosi-salygos" element={<Navigate to="/terms" replace />} />
                  <Route path="privatumo-politika" element={<Navigate to="/privacy" replace />} />
                  <Route path="aciu" element={<Navigate to="/thank-you" replace />} />
                  <Route path="instaliuoti" element={<Navigate to="/install" replace />} />
                  <Route path="priimti-kvietima" element={<LegacyRedirect from="/priimti-kvietima" to="/accept-invitation" />} />
                  <Route path="dovana-gauta" element={<LegacyRedirect from="/dovana-gauta" to="/gift-received" />} />
                  <Route path="zemelapis" element={<Navigate to="/map" replace />} />
                  <Route path="zemelapis/solo" element={<LegacyRedirect from="/zemelapis/solo" to="/map/solo" />} />
                  <Route path="zemelapis/pildyti/:sessionId" element={<LegacyRedirect from="/zemelapis/pildyti" to="/map/fill" />} />
                  <Route path="poros-zemelapis" element={<Navigate to="/couples-map" replace />} />
                  <Route path="poros-zemelapis/kodas" element={<LegacyRedirect from="/poros-zemelapis/kodas" to="/couples-map/code" />} />
                  <Route path="poros-zemelapis/pildyti/:pairId" element={<LegacyRedirect from="/poros-zemelapis/pildyti" to="/couples-map/fill" />} />
                  <Route path="archetipas" element={<Navigate to="/archetype" replace />} />
                  <Route path="archetipas/anketa" element={<Navigate to="/archetype/details" replace />} />
                  <Route path="archetipas/:slug" element={<LegacyRedirect from="/archetipas" to="/archetype" />} />

                  <Route path="naujienlaiskis" element={<Navigate to="/" replace />} />
                  <Route path="naujienlaiskis/:slug" element={<Navigate to="/" replace />} />


                </Route>
                
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </CartProvider>
      </CurrencyProvider>
    </AuthProvider>
  </QueryClientProvider>
  </HelmetProvider>
);

export default App;
